import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

export class AnnouncementBar extends Component {
  #current = 0;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #scrollEndTimeout;

  /** @type {number | undefined} */
  #scrollRaf;

  /** @type {HTMLElement[] | null} */
  #slidesCache = null;

  #userInteracting = false;

  #scroller() {
    return this.querySelector(':scope .announcement-bar__slides');
  }

  /** @returns {HTMLElement[]} */
  #slides() {
    if (this.#slidesCache) return this.#slidesCache;

    const scroller = this.#scroller();
    if (!(scroller instanceof Element)) return [];

    this.#slidesCache = /** @type {HTMLElement[]} */ (
      [...scroller.querySelectorAll('.announcement-bar__slide')].filter((slide) => slide instanceof HTMLElement)
    );
    return this.#slidesCache;
  }

  #invalidateSlidesCache() {
    this.#slidesCache = null;
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {number} activeSlideIndex
   */
  #syncSlideAccessibility(slides, activeSlideIndex) {
    for (const [slideIndex, slide] of slides.entries()) {
      const hidden = slideIndex !== activeSlideIndex;
      slide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      slide.toggleAttribute('inert', hidden);
    }
  }

  #syncFromScroll() {
    const scroller = this.#scroller();
    const slides = this.#slides();
    if (!(scroller instanceof HTMLElement) || slides.length === 0) return;

    const slideWidth = scroller.clientWidth;
    if (slideWidth === 0) return;

    const maxIndex = slides.length - 1;
    const activeSlideIndex = Math.max(0, Math.min(Math.round(scroller.scrollLeft / slideWidth), maxIndex));

    this.#current = activeSlideIndex;
    this.#syncSlideAccessibility(slides, activeSlideIndex);
  }

  /**
   * @param {number} unboundedSlideIndex
   * @param {{ behavior?: ScrollBehavior }} [options]
   */
  #goToSlide(unboundedSlideIndex, options = {}) {
    const scroller = this.#scroller();
    const slides = this.#slides();
    if (!(scroller instanceof HTMLElement) || slides.length === 0) return;

    const slideCount = slides.length;
    let activeSlideIndex = unboundedSlideIndex % slideCount;
    if (activeSlideIndex < 0) activeSlideIndex += slideCount;

    this.#current = activeSlideIndex;

    const behavior = prefersReducedMotion() ? 'instant' : (options.behavior ?? 'smooth');
    scroller.scrollTo({ left: activeSlideIndex * scroller.clientWidth, behavior });
    this.#syncFromScroll();
  }

  connectedCallback() {
    super.connectedCallback();

    const scroller = this.#scroller();

    if (scroller instanceof HTMLElement) {
      scroller.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#resizeObserver = new ResizeObserver(() => this.#syncFromScroll());
      this.#resizeObserver.observe(scroller);
    }

    if (this.autoplay) {
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
      document.addEventListener('visibilitychange', this.#onVisibility);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncFromScroll();
        this.#startAutoplay();
      });
    });
  }

  disconnectedCallback() {
    const scroller = this.#scroller();
    if (scroller instanceof HTMLElement) {
      scroller.removeEventListener('scroll', this.#onScroll);
    }

    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    clearTimeout(this.#scrollEndTimeout);
    this.#scrollEndTimeout = undefined;

    if (this.#scrollRaf) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = undefined;
    }

    this.#invalidateSlidesCache();
    this.suspend();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#invalidateSlidesCache();
    this.#syncFromScroll();
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.#userInteracting) return;
    this.suspend();
    this.#startAutoplay();
  }

  #startAutoplay() {
    const intervalMs = this.autoplayInterval;
    if (!intervalMs || prefersReducedMotion()) return;

    const slides = this.#slides();
    if (slides.length < 2) return;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden || this.#userInteracting) return;
      this.#goToSlide(this.#current + 1);
    }, intervalMs);
  }

  get autoplayInterval() {
    const value = parseInt(`${this.getAttribute('autoplay')}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  #onScroll = () => {
    this.#userInteracting = true;
    clearTimeout(this.#scrollEndTimeout);

    if (this.#scrollRaf) return;

    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = undefined;
      this.#syncFromScroll();
    });

    this.#scrollEndTimeout = setTimeout(() => {
      this.#userInteracting = false;
      this.resume();
    }, 150);
  };

  #onVisibility = () => {
    if (document.hidden) {
      this.suspend();
    } else {
      this.resume();
    }
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
